import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, CheckCircle, XCircle, AlertTriangle, ShieldAlert } from 'lucide-react';
import { useRelatorioInventario, useExportarInventarioCsv } from '../hooks';
import { InventoryStatusBadge, InventoryResultBadge } from '../components/InventoryStatusBadge';
import { formatarData } from '../utils/formatters';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { EmptyState } from '../../../shared/components/EmptyState';

export default function InventarioReportPage() {
  const { id = '' } = useParams();
  const q = useRelatorioInventario(id);
  const exportCsv = useExportarInventarioCsv();
  const [banner, setBanner] = useState<string | null>(null);

  if (q.isLoading) {
    return (
      <PageContainer title="Carregando…">
        <div className="h-64 animate-pulse rounded bg-gray-100" />
      </PageContainer>
    );
  }
  if (q.isError || !q.data) {
    return (
      <PageContainer title="Relatório indisponível">
        <Alert variant="error">Falha ao carregar relatório.</Alert>
      </PageContainer>
    );
  }

  const { inventory, totals, items } = q.data;
  const found = items.filter((i) => i.result === 'found');
  const notFound = items.filter((i) => i.result === 'not_found');
  const divergent = items.filter((i) => i.result === 'divergent');
  const damaged = items.filter((i) => i.result === 'damaged');

  async function handleExport() {
    setBanner(null);
    try {
      const blob = await exportCsv.mutateAsync(id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inventario-${inventory.name.replace(/\s+/g, '_')}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setBanner('Arquivo baixado.');
    } catch {
      setBanner('Falha ao exportar CSV.');
    }
  }

  return (
    <PageContainer
      title={`Relatório — ${inventory.name}`}
      breadcrumbs={[
        { label: 'Ativos', to: '/ativos' },
        { label: 'Inventário', to: '/ativos/inventarios' },
        { label: inventory.name, to: `/ativos/inventarios/${id}` },
        { label: 'Relatório' },
      ]}
      action={
        <div className="flex items-center gap-2">
          <InventoryStatusBadge status={inventory.status} />
          <Button
            variant="secondary"
            icon={<Download className="h-4 w-4" />}
            loading={exportCsv.isPending}
            onClick={handleExport}
            className="!border-green-300 !text-green-700 hover:!bg-green-50"
          >
            {exportCsv.isPending ? 'Exportando…' : 'Exportar CSV'}
          </Button>
        </div>
      }
    >
      {banner && (
        <Alert
          variant="info"
          action={<Button variant="ghost" size="sm" onClick={() => setBanner(null)}>fechar</Button>}
        >
          {banner}
        </Alert>
      )}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-green-800">Localizados</p>
          <p className="mt-1 text-3xl font-bold text-green-900 flex items-center gap-2">
            <CheckCircle className="h-6 w-6" /> {totals.found}
          </p>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-red-800">Não localizados</p>
          <p className="mt-1 text-3xl font-bold text-red-900 flex items-center gap-2">
            <XCircle className="h-6 w-6" /> {totals.not_found}
          </p>
        </div>
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-orange-800">Divergências</p>
          <p className="mt-1 text-3xl font-bold text-orange-900 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6" /> {totals.divergent}
          </p>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-red-800">Danificados</p>
          <p className="mt-1 text-3xl font-bold text-red-900 flex items-center gap-2">
            <ShieldAlert className="h-6 w-6" /> {totals.damaged}
          </p>
        </div>
      </section>

      {[
        { label: 'Ativos localizados', color: 'green', items: found },
        { label: 'Ativos não localizados', color: 'red', items: notFound },
        { label: 'Divergências patrimoniais', color: 'orange', items: divergent },
        { label: 'Ativos danificados', color: 'red', items: damaged },
      ].map((block) => (
        <section key={block.label} className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <h3 className={`border-b border-gray-200 px-4 py-2 text-sm font-semibold text-${block.color}-800`}>{block.label} ({block.items.length})</h3>
          {block.items.length === 0 ? (
            <EmptyState title="Nenhum item nesta categoria" size="compact" />
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left">Código</th>
                  <th className="px-4 py-2 text-left">Descrição</th>
                  <th className="px-4 py-2 text-left">Responsável esperado</th>
                  <th className="px-4 py-2 text-left">Localização esperada</th>
                  <th className="px-4 py-2 text-left">Resultado</th>
                  <th className="px-4 py-2 text-left">Observação</th>
                  <th className="px-4 py-2 text-left">Conferido em</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {block.items.map((it) => (
                  <tr key={it.id}>
                    <td className="px-4 py-2 font-mono text-gray-800">{it.asset?.displayCode ?? it.asset?.assetCode}</td>
                    <td className="px-4 py-2 text-gray-700">{it.asset?.description}</td>
                    <td className="px-4 py-2 text-gray-700">{it.expectedCustodian?.name ?? '—'}</td>
                    <td className="px-4 py-2 text-gray-700">{it.expectedLocation?.name ?? '—'}</td>
                    <td className="px-4 py-2"><InventoryResultBadge result={it.result} /></td>
                    <td className="px-4 py-2 text-gray-700">{it.divergenceNotes ?? '—'}</td>
                    <td className="px-4 py-2 text-gray-700">{it.checkedAt ? formatarData(it.checkedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ))}
    </PageContainer>
  );
}
